/**
 * 角色权限管理服务 - 后端 API 对接层
 *
 * 【修复角色权限三审#13】删除 VITE_USE_MOCK 双轨分支与 mock/role.ts 假实现：
 * 本域 mock（含 mock/role-data.ts 的角色预设表）与后端 RolePresetConfig 重复维护必然漂移，
 * 统一直连真实接口；后端 {code, message, data} 已由 httpClient 拦截器解包为业务数据，
 * 此处仅转换为前端统一格式 {success, data, error}。
 */

import { httpGet, httpPost, httpPut, httpDelete } from '@/utils/httpClient'
import { wrapResponse, type ApiResponse } from '@/services/types'
import type {
  Role,
  CreateRoleDto,
  UpdateRoleDto,
  Permission,
  PermissionTemplate,
  AssignedUser,
  UnassignedUser,
  ImportResult,
} from '@/types/role'

// ===== 角色 CRUD =====

export async function getRoles(params?: {
  keyword?: string
  type?: 'all' | 'builtin' | 'custom'
}): Promise<ApiResponse<Role[]>> {
  const data = await httpGet<Role[]>('/api/admin/roles', { params })
  return wrapResponse(data)
}

export async function getRoleById(id: string): Promise<ApiResponse<Role | null>> {
  const data = await httpGet<Role | null>(`/api/admin/roles/${id}`)
  return wrapResponse(data)
}

export async function createRole(data: CreateRoleDto): Promise<ApiResponse<Role>> {
  const res = await httpPost<Role>('/api/admin/roles', data)
  return wrapResponse(res)
}

export async function updateRole(id: string, data: UpdateRoleDto): Promise<ApiResponse<Role>> {
  const res = await httpPut<Role>(`/api/admin/roles/${id}`, data)
  return wrapResponse(res)
}

export async function deleteRole(
  id: string,
  transferToRoleId?: string
): Promise<ApiResponse<void>> {
  await httpDelete<void>(`/api/admin/roles/${id}`, {
    params: transferToRoleId ? { transferToRoleId } : undefined,
  })
  return wrapResponse(undefined as void)
}

export async function cloneRole(
  sourceRoleId: string,
  data: { name: string; code: string; scope: 'admin' | 'workspace'; description?: string }
): Promise<ApiResponse<Role>> {
  const res = await httpPost<Role>(`/api/admin/roles/${sourceRoleId}/clone`, data)
  return wrapResponse(res)
}

// ===== 用户分配 =====

export async function getAssignedUsers(roleId: string): Promise<ApiResponse<AssignedUser[]>> {
  const data = await httpGet<AssignedUser[]>(`/api/admin/roles/${roleId}/users`)
  return wrapResponse(data)
}

export async function getAvailableUsers(
  roleId: string,
  keyword?: string
): Promise<ApiResponse<UnassignedUser[]>> {
  const data = await httpGet<UnassignedUser[]>(`/api/admin/roles/${roleId}/available-users`, {
    params: keyword ? { keyword } : undefined,
  })
  return wrapResponse(data)
}

export async function assignUsersToRole(
  roleId: string,
  userIds: string[]
): Promise<ApiResponse<void>> {
  await httpPost<void>(`/api/admin/roles/${roleId}/users`, { userIds })
  return wrapResponse(undefined as void)
}

export async function removeUsersFromRole(
  roleId: string,
  userIds: string[]
): Promise<ApiResponse<void>> {
  await httpDelete<void>(`/api/admin/roles/${roleId}/users`, { data: { userIds } })
  return wrapResponse(undefined as void)
}

// ===== 权限管理 =====

export async function getPermissionTree(): Promise<ApiResponse<Permission[]>> {
  const data = await httpGet<Permission[]>('/api/admin/permissions/tree')
  return wrapResponse(data)
}

export async function getRolePermissions(roleId: string): Promise<ApiResponse<string[]>> {
  const res = await httpGet<{ roleId: string; permissionIds: string[] }>(
    `/api/admin/roles/${roleId}/permissions`
  )
  return wrapResponse(res.permissionIds)
}

export async function setRolePermissions(
  roleId: string,
  permissionIds: string[]
): Promise<ApiResponse<void>> {
  await httpPut<void>(`/api/admin/roles/${roleId}/permissions`, { permissionIds })
  return wrapResponse(undefined as void)
}

// ===== 模板功能（对接后端 PermissionTemplateService） =====

/** 保存角色权限为模板 POST /api/admin/roles/{id}/template */
export async function saveAsTemplate(
  sourceRoleId: string,
  templateName: string
): Promise<ApiResponse<PermissionTemplate>> {
  const res = await httpPost<PermissionTemplate>(
    `/api/admin/roles/${sourceRoleId}/template?name=${encodeURIComponent(templateName)}`
  )
  return wrapResponse(res)
}

/** 获取所有模板列表 GET /api/admin/roles/templates */
export async function getTemplates(): Promise<ApiResponse<PermissionTemplate[]>> {
  const data = await httpGet<PermissionTemplate[]>('/api/admin/roles/templates')
  return wrapResponse(data)
}

/** 删除指定模板 DELETE /api/admin/roles/templates/{id} */
export async function deleteTemplate(id: string): Promise<ApiResponse<void>> {
  await httpDelete<void>(`/api/admin/roles/templates/${id}`)
  return wrapResponse(undefined as void)
}

/** 从模板创建角色 POST /api/admin/roles/from-template */
export async function createRoleFromTemplate(
  templateId: string,
  data: { name: string; code: string; scope: 'admin' | 'workspace'; description?: string }
): Promise<ApiResponse<Role>> {
  const res = await httpPost<Role>(
    `/api/admin/roles/from-template?templateId=${encodeURIComponent(templateId)}`,
    data
  )
  return wrapResponse(res)
}

// ===== 导入导出（对接后端 RoleExportImportService） =====

/** 导出所有角色到 Excel GET /api/admin/roles/export（拦截器对 blob 走 pass-through） */
export async function exportRolesToExcel(): Promise<ApiResponse<Blob>> {
  const blob = await httpGet<Blob>('/api/admin/roles/export', { responseType: 'blob' })
  return wrapResponse(blob)
}

/** 下载角色导入模板 GET /api/admin/roles/import-template */
export async function downloadImportTemplate(): Promise<ApiResponse<Blob>> {
  const blob = await httpGet<Blob>('/api/admin/roles/import-template', { responseType: 'blob' })
  return wrapResponse(blob)
}

/** 从 Excel 文件导入角色 POST /api/admin/roles/import */
export async function importRolesFromExcel(file: File): Promise<ApiResponse<ImportResult>> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await httpPost<ImportResult>('/api/admin/roles/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return wrapResponse(res)
}
